/**
 * Avatar3DSelector - Componente para seleção de avatares 3D
 * 
 * Exibe grid de avatares 3D disponíveis com opção de upload customizado.
 * Usado em EditAgentDialog, UserProfileDialog e SellerFormDialog.
 * 
 * Hierarquia de seleção:
 * 1. Imagem customizada (upload)
 * 2. Avatar 3D pré-definido
 * 
 * @version 1.0.0
 */

import * as React from "react";
import { cn } from "@/lib/utils";
import { getAll3DAvatars, is3DAvatarId } from "@/lib/utils/avatar-3d";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Camera, Check, X } from "lucide-react";

// Tipos de arquivo aceitos para upload
const ACCEPTED_IMAGE_TYPES = "image/jpeg,image/png,image/webp,image/gif";
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export interface Avatar3DSelectorProps {
  /** 
   * Valor atual - pode ser:
   * - ID de avatar 3D (ex: "avatar-3")
   * - Path de storage (ex: "users/abc/avatar.jpg")
   * - null (nenhum selecionado)
   */
  value: string | null;
  
  /** Callback quando um avatar 3D é selecionado */
  onChange: (avatarId: string | null) => void;
  
  /** Callback quando um arquivo customizado é selecionado */
  onCustomUpload?: (file: File) => void;
  
  /** URL de preview da imagem customizada */
  customPreviewUrl?: string | null;
  
  /** Callback para limpar imagem customizada */
  onClearCustom?: () => void;
  
  /** Desabilita interações */
  disabled?: boolean;
  
  /** Label do campo */
  label?: string;
  
  /** Indica se a seleção é obrigatória */
  required?: boolean;
  
  /** Indica se há erro de validação */
  error?: boolean;
  
  /** Classe adicional para o container */
  className?: string;
}

/**
 * Componente de seleção de avatar 3D com suporte a upload customizado
 */
export const Avatar3DSelector = React.forwardRef<HTMLDivElement, Avatar3DSelectorProps>(
  (
    {
      value,
      onChange,
      onCustomUpload,
      customPreviewUrl,
      onClearCustom,
      disabled = false,
      label = "Avatar",
      required = false,
      error = false,
      className,
    },
    ref
  ) => {
    // Referência para o input de arquivo
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    
    // Busca todos os avatares 3D disponíveis
    const avatars = React.useMemo(() => getAll3DAvatars(), []);
    
    // Verifica se o valor atual é um avatar 3D
    const isValue3DAvatar = is3DAvatarId(value);
    
    // Handler para seleção de avatar 3D
    const handleSelectAvatar = (avatarId: string) => {
      if (disabled) return;
      
      // Se já está selecionado, desseleciona
      if (value === avatarId) {
        onChange(null);
      } else {
        // Limpa imagem customizada se houver
        if (onClearCustom && customPreviewUrl) {
          onClearCustom();
        }
        onChange(avatarId);
      }
    };
    
    // Handler para clique no botão de upload
    const handleUploadClick = () => {
      if (disabled) return;
      fileInputRef.current?.click();
    };
    
    // Handler para seleção de arquivo
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      
      // Validação de tipo
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione uma imagem válida.');
        return;
      }
      
      // Validação de tamanho
      if (file.size > MAX_FILE_SIZE) {
        alert('A imagem deve ter no máximo 5MB.');
        return;
      }
      
      // Notifica o componente pai
      if (onCustomUpload) {
        // Limpa seleção de avatar 3D
        onChange(null);
        onCustomUpload(file);
      }
      
      // Limpa o input para permitir reselecionar o mesmo arquivo
      event.target.value = '';
    };
    
    // Handler para limpar imagem customizada
    const handleClearCustom = () => {
      if (disabled) return;
      if (onClearCustom) {
        onClearCustom();
      }
    };

    // Verifica se tem avatar selecionado
    const hasSelection = !!value || !!customPreviewUrl;
    const showError = required && !hasSelection && error;

    return (
      <div ref={ref} className={cn("space-y-3", className)}>
        <Label>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
        
        {/* Preview da imagem customizada (se houver) */}
        {customPreviewUrl && (
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border">
            <div className="relative">
              <img
                src={customPreviewUrl}
                alt="Avatar customizado"
                className="h-16 w-16 rounded-full object-cover border-2 border-primary"
              />
              {!disabled && (
                <button
                  type="button"
                  onClick={handleClearCustom}
                  className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90 transition-colors"
                  aria-label="Remover imagem"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            <div className="text-sm">
              <p className="font-medium text-foreground">Imagem personalizada</p>
              <p className="text-muted-foreground">Sua foto será usada como avatar</p>
            </div>
          </div>
        )}
        
        {/* Grid de avatares 3D */}
        <div className="grid grid-cols-4 gap-2">
          {avatars.map((avatar) => {
            const isSelected = value === avatar.id && !customPreviewUrl;
            
            return (
              <button
                key={avatar.id}
                type="button"
                onClick={() => handleSelectAvatar(avatar.id)}
                disabled={disabled}
                className={cn(
                  "relative aspect-square rounded-lg overflow-hidden border-2 transition-all",
                  "hover:scale-105 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                  isSelected
                    ? "border-primary ring-2 ring-primary ring-offset-2"
                    : "border-border hover:border-primary/50",
                  disabled && "opacity-50 cursor-not-allowed hover:scale-100"
                )}
                aria-label={`Selecionar ${avatar.id}`}
                aria-pressed={isSelected}
              >
                <img
                  src={avatar.url}
                  alt={avatar.id}
                  className="h-full w-full object-cover"
                />
                
                {/* Indicador de seleção */}
                {isSelected && (
                  <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                    <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center">
                      <Check className="h-4 w-4 text-primary-foreground" />
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        
        {/* Botão para upload de imagem customizada */}
        {onCustomUpload && (
          <>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">ou</span>
              </div>
            </div>
            
            <Button
              type="button"
              variant="outline"
              onClick={handleUploadClick}
              disabled={disabled}
              className="w-full"
            >
              <Camera className="h-4 w-4 mr-2" />
              Enviar foto própria
            </Button>
            
            {/* Input de arquivo oculto */}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES}
              onChange={handleFileChange}
              className="hidden"
              aria-hidden="true"
            />
          </>
        )}
        
        {/* Mensagem de erro ou instrução */}
        {showError ? (
          <p className="text-xs text-destructive font-medium">
            Selecione um avatar ou envie uma foto.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Escolha um avatar 3D ou envie sua própria foto.
          </p>
        )}
      </div>
    );
  }
);

Avatar3DSelector.displayName = "Avatar3DSelector";
